import aiAssistantAuraConfig from '../../consts/aiAssistant/aiAssistantAuraConfig';

/**
 * Pure math for the aura: a shared orbit plus non-repeating per-blob wander, both scaled by the voice level.
 * Level 0 is the idle pose, so the aura keeps moving in silence.
 */

const {
    ORBIT_X,
    ORBIT_Y,
    ORBIT_SCALE,
    REST_AMPLITUDE,
    LEVEL_AMPLITUDE,
    WANDER_WAVES,
    WANDER_MIN_HZ,
    WANDER_MAX_HZ,
    WANDER_X,
    WANDER_Y,
    WANDER_SCALE,
    WANDER_OPACITY,
    WANDER_PACE,
    SPEED_GAIN,
    REST_OPACITY,
    FIELD_REST_SCALE_Y,
    FIELD_LEVEL_SCALE_Y,
} = aiAssistantAuraConfig;

const TWO_PI = 2 * Math.PI;

const clamp01 = (value) => Math.min(Math.max(value, 0), 1);

// Mean of random sines: never repeats, stays within -1..1
const createWanderChannel = () => {
    const waves = Array.from({length: WANDER_WAVES}, () => ({
        frequencyHz: WANDER_MIN_HZ + Math.random() * (WANDER_MAX_HZ - WANDER_MIN_HZ),
        offset: Math.random() * TWO_PI,
    }));

    return (elapsedS) =>
        waves.reduce((sum, {frequencyHz, offset}) => sum + Math.sin(TWO_PI * frequencyHz * elapsedS + offset), 0) /
        waves.length;
};

const getAmplitude = (level) => REST_AMPLITUDE + LEVEL_AMPLITUDE * level;

// Cycles per BASE_CYCLE_S; positive since WANDER_PACE < 1
const getPace = (level, wanderPace) => (1 + SPEED_GAIN * level) * (1 + WANDER_PACE * wanderPace);

// sin(2 * angle): swells twice per cycle, so size doesn't just follow position
const getBlobFrame = ({reach, glow, phaseOffset}, {level, phase, wanderX, wanderY}) => {
    const angle = TWO_PI * (phase + phaseOffset);
    const amplitude = getAmplitude(level);

    return {
        x: amplitude * (ORBIT_X * Math.cos(angle) + WANDER_X * wanderX),
        y: amplitude * (ORBIT_Y * Math.sin(angle) + WANDER_Y * wanderY),
        scale: 1 + reach * level + amplitude * ORBIT_SCALE * Math.sin(2 * angle) + WANDER_SCALE * wanderY,
        opacity: clamp01(REST_OPACITY + glow * level + WANDER_OPACITY * wanderX),
    };
};

const getFieldScaleY = (level) => FIELD_REST_SCALE_Y + FIELD_LEVEL_SCALE_Y * level;

export default {createWanderChannel, getAmplitude, getPace, getBlobFrame, getFieldScaleY};
